import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
} from "@heroui/react";
import { cn } from "@heroui/react";
import { EnvelopeFill, BellFill, CheckCircleFill } from "react-bootstrap-icons";

import { NotifyModalSchema, NotifyModalFormData } from "@/lib/form-schemas";

interface NotifyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string) => void;
  recommendationName: string;
  actionName: string;
}

export function NotifyModal({
  isOpen,
  onClose,
  onSubmit,
  recommendationName,
  actionName,
}: NotifyModalProps) {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<NotifyModalFormData>({
    resolver: zodResolver(NotifyModalSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmitForm = async (data: NotifyModalFormData) => {
    try {
      // Store email for success message
      setSubmittedEmail(data.email.trim());

      // Enhanced console log with all available details
      // eslint-disable-next-line no-console
      console.log("NotifyModal - Detailed submission data:", {
        email: data.email.trim(),
        recommendation: recommendationName,
        action: actionName,
        timestamp: new Date().toISOString(),
        userId: localStorage.getItem("crowdsec_user_id"),
        sessionId: localStorage.getItem("crowdsec_session_id"),
        userAgent: navigator.userAgent,
        referrer: document.referrer || "direct",
        currentUrl: window.location.href,
        emailDomain: data.email.includes("@")
          ? data.email.split("@")[1]
          : "unknown",
      });

      // Call the onSubmit callback
      onSubmit(data.email.trim());

      // Show success state instead of immediately closing
      setIsSubmitted(true);

      // Auto-close after 6 seconds
      setTimeout(() => {
        handleClose();
      }, 6000);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("NotifyModal - Submission error:", error);
      // Form errors are handled by react-hook-form
    }
  };

  const handleClose = () => {
    reset();
    setIsSubmitted(false);
    setSubmittedEmail("");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} placement="center" size="lg" onClose={handleClose}>
      <ModalContent className="p-2">
        {!isSubmitted ? (
          // Form state
          <form onSubmit={handleSubmit(onSubmitForm)}>
            <ModalHeader className="flex flex-col gap-2 pb-4">
              <div className="flex items-center gap-4">
                <BellFill className="text-primary text-2xl" />
                <div>
                  <h3 className="text-2xl font-bold">Get Notified</h3>
                  <p className="text-base text-default-500">
                    {recommendationName} - {actionName}
                  </p>
                </div>
              </div>
            </ModalHeader>
            <ModalBody className="py-6">
              <div className="space-y-6">
                <p className="text-base text-default-600 leading-relaxed">
                  Get notified when{" "}
                  <span className="font-medium">{recommendationName}</span>{" "}
                  becomes available. Enter your email to be the first to know!
                </p>
                <Input
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus // Acceptable for modal context - improves UX
                  classNames={{
                    input: "text-lg",
                    inputWrapper: "h-14",
                  }}
                  endContent={
                    <EnvelopeFill className="text-default-400 text-xl" />
                  }
                  errorMessage={errors.email?.message}
                  isInvalid={!!errors.email}
                  label="Email Address"
                  labelPlacement="outside"
                  placeholder="your.email@company.com"
                  size="lg"
                  type="email"
                  variant="bordered"
                  {...register("email")}
                />

                {/* GDPR Privacy Notice */}
                <div className="bg-gray-500/5 border-l-4 border-gray-400 p-4 rounded">
                  <p className="text-xs text-gray-400">
                    <strong>Privacy Notice:</strong> Your email address will be
                    used solely to notify you when this recommendation becomes
                    available. We will not share your information with third
                    parties or use it for marketing purposes. Your data is
                    processed securely and you can request its deletion at any
                    time by contacting us.
                  </p>
                </div>
              </div>
            </ModalBody>
            <ModalFooter className="gap-4 pt-6">
              <Button
                color="danger"
                size="lg"
                variant="light"
                onPress={handleClose}
              >
                Cancel
              </Button>
              <Button
                className={cn("font-medium px-8")}
                color="primary"
                isLoading={isSubmitting}
                size="lg"
                type="submit"
              >
                {isSubmitting ? "Submitting..." : "Notify Me"}
              </Button>
            </ModalFooter>
          </form>
        ) : (
          // Success state
          <>
            <ModalHeader className="flex flex-col gap-2 pb-4">
              <div className="flex items-center gap-4">
                <CheckCircleFill className="text-success text-3xl" />
                <div>
                  <h3 className="text-2xl font-bold">You&apos;re All Set!</h3>
                  <p className="text-base text-default-500">
                    Notification Registered
                  </p>
                </div>
              </div>
            </ModalHeader>
            <ModalBody className="py-6">
              <div className="space-y-6">
                <p className="text-base text-default-600 leading-relaxed">
                  Perfect! We&apos;ll notify you as soon as{" "}
                  <span className="font-medium">{recommendationName}</span>{" "}
                  becomes available.
                </p>
                <p className="text-base text-default-600 leading-relaxed">
                  You&apos;ll receive a notification at{" "}
                  <span className="font-medium text-primary">
                    {submittedEmail}
                  </span>{" "}
                  when it&apos;s ready.
                </p>
                <p className="text-sm text-default-500 text-center">
                  This window will close automatically in a few seconds...
                </p>
              </div>
            </ModalBody>
            <ModalFooter className="pt-6">
              <Button
                className={cn("w-full font-medium")}
                color="primary"
                size="lg"
                variant="light"
                onPress={handleClose}
              >
                Close
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
